import jsPDF from 'jspdf';
import { montoEnLetras, conceptoDefault } from 'shared/receipt';
import { projectLogoUrl, type ContractDetail, type PaymentRow } from '@/lib/api';
import { fmtMoney, fmtDate } from '@/utils/format';

const THEME_BRAND = '#2952b3';

async function loadLogoDataUrl(projectId: string): Promise<{ dataUrl: string; format: 'PNG' | 'JPEG' } | null> {
  try {
    const res = await fetch(projectLogoUrl(projectId), { credentials: 'include' });
    if (!res.ok) return null;
    const blob = await res.blob();
    const format = blob.type === 'image/png' ? 'PNG' : 'JPEG';
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result as string);
      fr.onerror = reject;
      fr.readAsDataURL(blob);
    });
    return { dataUrl, format };
  } catch {
    return null;
  }
}

/** Draw a labeled cell: light label on top, bold value below, inside [x,y,w,h] (mm). */
function cell(doc: jsPDF, x: number, y: number, w: number, h: number, label: string, value: string) {
  doc.setDrawColor('#c8cee0');
  doc.rect(x, y, w, h);
  doc.setFontSize(6.5);
  doc.setTextColor('#5a6477');
  doc.text(label.toUpperCase(), x + 1.5, y + 3);
  doc.setTextColor('#1a1f2c');
  doc.setFont('helvetica', 'bold');

  // Keep the value at full 9pt. A short value stays on one line at the bottom of
  // the box; a long one (e.g. the Vendedor "María Fernanda Gonzáles de la Cruz
  // Hernández") wraps and the block grows UPWARD from a fixed bottom baseline, so
  // it stays inside the cell instead of spilling past the bottom border. The last
  // line always sits at y+h-2, lining up with the single-line cells beside it.
  const text = value || '';
  const avail = w - 3;          // horizontal room (mm)
  doc.setFontSize(9);
  let lines = doc.splitTextToSize(text, avail);
  const lineH = doc.getTextDimensions(text || ' ').h; // one-line height at 9pt
  // The cell only has vertical room for ~2 lines before colliding with the label.
  if (lines.length > 2) lines = lines.slice(0, 2);
  const startY = y + h - 2 - (lines.length - 1) * lineH;
  doc.text(lines, x + 1.5, startY);

  doc.setFont('helvetica', 'normal');
}

export async function exportPaymentReceiptPDF(args: {
  contract: ContractDetail;
  payment: PaymentRow;
  municipalityName?: string | null;
}): Promise<void> {
  const { contract, payment, municipalityName } = args;
  const brand = payment.receiptMeta?.branding.brandColor ?? THEME_BRAND;
  const footer = payment.receiptMeta?.branding.footer ?? '';
  const lot = contract.lot;
  const meta = payment.receiptMeta;
  // A cash sale (pago de contado) settles in full at once — running-balance and
  // installment fields don't apply, so they read "—" (N/A) instead of a synthetic saldo/cuota.
  const isCash = contract.saleType === 'cash';
  const payer = payment.payerName ?? contract.buyerName;
  const concepto = payment.concept ?? conceptoDefault({ cuotaNumber: meta?.cuotaNumber ?? 0, lotNumber: lot?.number, manzana: lot?.manzana });
  const [yy, mm, dd] = payment.paymentDate.split('-');

  // Half US Letter (landscape): 215.9 x 139.7 mm.
  const doc = new jsPDF({ unit: 'mm', format: [215.9, 139.7], orientation: 'landscape' });
  const M = 8;               // page margin
  const W = 215.9 - M * 2;   // content width
  let y = M;

  // Header band + logo.
  doc.setFillColor(brand);
  doc.rect(M, y, 70, 12, 'F');
  doc.setTextColor('#ffffff');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.text('Recibo', M + 4, y + 8);
  doc.setTextColor('#1a1f2c');
  doc.setFontSize(9);
  doc.text(`No. ${payment.receiptNumber ?? ''}`, M + 74, y + 8);
  const logo = await loadLogoDataUrl(contract.projectId);
  if (logo) { try { doc.addImage(logo.dataUrl, logo.format, M + W - 40, y, 40, 14); } catch { /* skip bad image */ } }
  y += 16;

  // Row 1: Ciudad | Día | Mes | Año | No. de Lote | Letra | Valor de Lote
  const r1 = y; const h = 11;
  cell(doc, M, r1, 40, h, 'Ciudad', municipalityName ?? '');
  cell(doc, M + 40, r1, 18, h, 'Día', dd ?? '');
  cell(doc, M + 58, r1, 18, h, 'Mes', mm ?? '');
  cell(doc, M + 76, r1, 22, h, 'Año', yy ?? '');
  cell(doc, M + 98, r1, 34, h, 'No. de Lote', lot?.number ?? '');
  cell(doc, M + 132, r1, 24, h, 'Letra', lot?.manzana ?? '');
  cell(doc, M + 156, r1, W - 156, h, 'Valor de Lote', lot?.listPrice ? fmtMoney(lot.listPrice) : '');
  y += h;

  // Row 2: Recibí de | TEL | Vendedor | Saldo a la fecha | Pago actual
  cell(doc, M, y, 60, h, 'Recibí de', payer);
  cell(doc, M + 60, y, 34, h, 'Tel', (contract as unknown as { buyerPhone?: string | null }).buyerPhone ?? '');
  cell(doc, M + 94, y, 40, h, 'Vendedor', contract.sellerName ?? '');
  cell(doc, M + 134, y, 32, h, 'Saldo a la fecha', isCash ? '—' : meta ? fmtMoney(meta.saldoAnterior) : '');
  cell(doc, M + 166, y, W - 166, h, 'Pago actual', fmtMoney(payment.amount));
  y += h;

  // Row 3: Fecha de compra | Cantidad de Meses | Concepto (wide)
  cell(doc, M, y, 40, h, 'Fecha de compra', fmtDate(contract.startDate));
  cell(doc, M + 40, y, 30, h, 'Cantidad de Meses', isCash ? '—' : String(contract.termMonths));
  cell(doc, M + 70, y, W - 70, h, 'Concepto', concepto);
  y += h;

  // Row 4: No. de Cuota | Saldo Nuevo | Dirección (wide)
  cell(doc, M, y, 40, h, 'No. de Cuota', isCash ? '—' : meta ? `No. ${meta.cuotaNumber}` : '');
  cell(doc, M + 40, y, 40, h, 'Saldo Nuevo', isCash ? '—' : meta ? fmtMoney(meta.saldoNuevo) : '');
  cell(doc, M + 80, y, W - 80, h, 'Dirección', lot?.address ?? '');
  y += h;

  // Valor en letras.
  cell(doc, M, y, W, h + 3, 'Valor (en letras)', montoEnLetras(payment.amount));
  y += h + 8;

  // Signature lines.
  doc.setDrawColor('#1a1f2c');
  doc.line(M + 4, y + 10, M + 90, y + 10);
  doc.line(M + W - 90, y + 10, M + W - 4, y + 10);
  doc.setFontSize(8);
  doc.setTextColor('#1a1f2c');
  doc.text('Firma de recibido Representante', M + 4, y + 14);
  doc.text('Firma y Nombre de comprador', M + W - 90, y + 14);
  y += 18;

  // Footer (per-project) + fixed tagline.
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  if (footer) doc.text(footer, 215.9 / 2, y, { align: 'center', maxWidth: W });
  doc.setFont('helvetica', 'normal');
  doc.setTextColor('#5a6477');
  doc.setFontSize(7);
  doc.text('Reservas no están sujetas a devolución', 215.9 / 2, y + 5, { align: 'center' });

  // A voided payment must never produce a clean, unmarked official recibo — the
  // record stays auditable (nothing is deleted/regenerated), but it's stamped so
  // no one mistakes it for a valid receipt. Drawn last so it sits on top of everything.
  if (payment.status === 'voided') {
    doc.setGState(doc.GState({ opacity: 0.35 }));
    doc.setTextColor('#c0392b');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(60);
    doc.text('ANULADO', 215.9 / 2, 139.7 / 2, { align: 'center', angle: 30 });
    doc.setGState(doc.GState({ opacity: 1 }));
  }

  const filenameTag = payment.status === 'voided' ? 'ANULADO_' : '';
  doc.save(`Recibo_${filenameTag}${payment.receiptNumber ?? contract.contractNumber}.pdf`);
}
